import { createSelector } from '@reduxjs/toolkit';
import { Vector3Tuple } from 'three';

import type { RootState } from '@/stores/store';
import { CustomGeometryClassNames } from '@/types/custom';
import { createSelectorWeakMap } from '@nablax/shared/utils';

import { BBoxDescription } from '../../types/bboxes';
import {
  box3tuple,
  translateBox3,
  translateVector3,
} from '../../utils/vectors';

export const selectBboxesFeature = (s: RootState) => s.editor.bboxes;

export const selectMeshScale = createSelector(
  [selectBboxesFeature],
  (s) => s.view.scale,
);

export const selectMeshOffset = createSelector(
  [selectBboxesFeature],
  (s) => s.view.offset,
);

const _selectBaseNameParam = (_d: RootState, baseName: string) => baseName;
const _selectGeometryClassParam = (
  _: RootState,
  geometryClass: CustomGeometryClassNames,
) => geometryClass;
export const selectBboxDescription = createSelector(
  [_selectBaseNameParam, selectBboxesFeature],
  (baseName, s) => s.mesh[baseName] || null,
);

export const selectGeometryClassBboxDescription = createSelector(
  [_selectGeometryClassParam, selectBboxesFeature],
  (geometryClass, s) => s.geometryClasses[geometryClass] || null,
);

export const selectOffsetGeometryBBoxes = createSelectorWeakMap(
  [selectBboxesFeature, selectMeshOffset],
  (s, offset) => applyOffset(s.geometryClasses, offset),
);

export const selectOffsetMeshBBoxes = createSelectorWeakMap(
  [selectBboxesFeature, selectMeshOffset],
  (s, offset) => applyOffset(s.mesh, offset),
);

export const selectOffsetRootBbox = createSelector(
  [selectOffsetMeshBBoxes],
  (bboxes) => bboxes['$root'] || null,
);

const _selectBucketParam = (_: RootState, bucket: 'mesh' | 'geometryClass') =>
  bucket;
const _selectNameWBucketParam = (
  _: RootState,
  _1: 'mesh' | 'geometryClass',
  name: string,
) => name;
export const selectOffsetBBoxByName = createSelectorWeakMap(
  [
    _selectBucketParam,
    _selectNameWBucketParam,

    selectOffsetGeometryBBoxes,
    selectOffsetMeshBBoxes,
  ],
  (bucket, name, gbboxes, bboxes) =>
    (bucket === 'mesh' ? bboxes[name] : gbboxes[name]) || null,
);

const selectRootBbox = (state: RootState) =>
  selectBboxDescription(state, '$root');
/**
 * Mesh is ready when offset has been calculated:
 * main object3d can be shown
 */
export const selectRootMeshReady = createSelector(
  [selectRootBbox],
  (bboxDesc) => (bboxDesc && !!bboxDesc.bbox) || false,
);

function applyOffset(
  descs: { [key: string]: BBoxDescription | undefined },
  offset: Vector3Tuple,
): { [key: string]: BBoxDescription } {
  const result: { [key: string]: BBoxDescription } = {};
  for (const key in descs) {
    if (!Object.prototype.hasOwnProperty.call(descs, key)) {
      continue;
    }
    const desc = descs[key];
    result[key] = {
      ...desc!,
      bbox: box3tuple(translateBox3(desc!.bbox, offset)),
      center: translateVector3(desc!.center, offset).toArray(),
    };
  }
  return result;
}
